/**
 * SourceDetailPanel - Panel latéral slide-in pour détails source
 * Affiche les données brutes d'une source avec traçabilité et lien officiel
 * Mobile: modal fullscreen / Desktop: slide-in droite
 *
 * V2: Ajout logos officiels des sources
 */

import { useEffect, useRef } from 'react'
import { SourceINRS, SourceANSES, SourceECHA, SourceAGRITOX } from '../../services/api'
import { SOURCES_CONFIG, SourceKey, INRS_LAST_UPDATE } from '../../config/sources'
import { SourceLink } from '../sources/SourceBadge'

type SourceType = 'inrs' | 'anses' | 'echa' | 'agritox'

interface SourceDetailPanelProps {
  isOpen: boolean
  onClose: () => void
  sourceType: SourceType | null
  sources: {
    inrs: SourceINRS
    anses: SourceANSES
    echa: SourceECHA
    agritox: SourceAGRITOX
  }
}

// Formater une valeur avec unité
const formatValue = (value: { valeur: number; unite: string } | null | undefined): string => {
  if (!value) return 'Non documenté par les sources intégrées (V1)'
  return `${value.valeur} ${value.unite}`
}

export default function SourceDetailPanel({ isOpen, onClose, sourceType, sources }: SourceDetailPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null)

  // Fermer avec Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [isOpen, onClose])

  if (!isOpen || !sourceType) return null

  const config = SOURCES_CONFIG[sourceType as SourceKey]
  const source = sources[sourceType]

  // Contenu spécifique par source
  const renderSourceContent = () => {
    switch (sourceType) {
      case 'inrs': {
        const s = source as SourceINRS
        return (
          <div className="space-y-4">
            <DataRow label="VLEP-8h" value={formatValue(s.vlep_8h)} />
            <DataRow label="VLEP court terme" value={formatValue(s.vlep_court_terme)} />
            <DataRow label="Mentions" value={s.mentions || 'Non documenté par les sources intégrées (V1)'} />
            {s.message && <DataRow label="Note" value={s.message} />}
          </div>
        )
      }
      case 'anses': {
        const s = source as SourceANSES
        return (
          <div className="space-y-4">
            <DataRow label="VTR inhalation" value={formatValue(s.vtr_inhalation)} />
            <DataRow label="VTR orale" value={formatValue(s.vtr_orale)} />
            <DataRow label="Effet critique" value={s.effet_critique || 'Non documenté par les sources intégrées (V1)'} />
            {s.message && <DataRow label="Note" value={s.message} />}
          </div>
        )
      }
      case 'echa': {
        const s = source as SourceECHA
        return (
          <div className="space-y-4">
            <DataRow label="Substance SVHC" value={s.svhc ? 'Oui' : 'Non'} highlight={s.svhc} />
            <DataRow label="Raison d'inclusion" value={s.raison_inclusion || 'Non documenté par les sources intégrées (V1)'} />
            {s.message && <DataRow label="Note" value={s.message} />}
          </div>
        )
      }
      case 'agritox': {
        const s = source as SourceAGRITOX
        return (
          <div className="space-y-4">
            <DataRow label="DJA (Dose Journalière Admissible)" value={formatValue(s.dja)} />
            <DataRow label="ARfD (Dose de Référence Aiguë)" value={formatValue(s.arfd)} />
            <DataRow label="AOEL (Niveau Acceptable d'Exposition Opérateur)" value={formatValue(s.aoel)} />
            {s.message && <DataRow label="Note" value={s.message} />}
          </div>
        )
      }
    }
  }

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className={`
          fixed z-50 bg-white shadow-xl
          inset-0 md:inset-y-0 md:right-0 md:left-auto md:w-[480px]
          transform transition-transform duration-300
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
        role="dialog"
        aria-modal="true"
        aria-labelledby="panel-title"
      >
        {/* Header avec logo */}
        <div className="bg-white border-b-4 px-4 py-4" style={{ borderColor: config.color }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Logo officiel */}
              <img
                src={config.logo}
                alt={`Logo ${config.name}`}
                className="h-10 w-auto object-contain"
              />
              <div>
                <h2 id="panel-title" className="text-xl font-bold text-gray-900">
                  {config.name}
                </h2>
                <p className="text-sm text-gray-600">{config.fullName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Fermer"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto h-[calc(100%-200px)]">
          {/* Disponibilité */}
          {!source.disponible ? (
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <svg className="w-12 h-12 text-gray-300 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="text-gray-500 font-medium">Données non disponibles</p>
              <p className="text-sm text-gray-500 mt-1">
                Cette substance n'est pas référencée dans {config.name}
              </p>
            </div>
          ) : (
            renderSourceContent()
          )}
        </div>

        {/* Footer - Traçabilité + Lien source avec logo */}
        <div className="absolute bottom-0 left-0 right-0 bg-gray-50 border-t border-gray-200 p-4">
          {/* Date des données */}
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>
              Date des données:{' '}
              <strong>
                {sourceType === 'inrs'
                  ? `Mise à jour base: ${INRS_LAST_UPDATE}`
                  : source.date_donnees || 'Date non fournie par la source'}
              </strong>
            </span>
          </div>

          {/* Lien source officielle avec logo */}
          <SourceLink
            sourceKey={sourceType}
            substanceUrl={source.url}
          />
        </div>
      </div>
    </>
  )
}

// Composant ligne de données
function DataRow({ label, value, highlight = false }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="border-b border-gray-100 pb-3">
      <dt className="text-sm text-gray-500 mb-1">{label}</dt>
      <dd className={`font-medium ${highlight ? 'text-red-600' : 'text-gray-900'}`}>
        {value}
      </dd>
    </div>
  )
}
